import React, { useState, useEffect } from 'react';
import { UserWithPermissions, UpdatePermissionsRequest } from '../../types/user.types';
import { PermissionsSelector } from './PermissionsSelector';
import { NotificationPreferences } from './NotificationPreferences';

interface EditUserModalProps {
  isOpen: boolean;
  user: UserWithPermissions | null;
  onClose: () => void;
  onSubmit: (userId: string, data: UpdatePermissionsRequest) => Promise<void>;
  loading: boolean;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  user,
  onClose,
  onSubmit,
  loading,
}) => {
  const [formData, setFormData] = useState<UpdatePermissionsRequest>({
    permissions: [],
    notificationPreferences: {},
  });

  useEffect(() => {
    if (user) {
      setFormData({
        permissions: user.permissions || [],
        notificationPreferences: user.notificationPreferences || {},
      });
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;

    try {
      await onSubmit(user.id, formData);
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fadeIn">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
            <h2 className="text-2xl font-bold text-gray-900">Edit User Permissions</h2>
            <p className="text-sm text-gray-600 mt-1">{user.fullName} ({user.email})</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* User Info (Read-only) */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Role:</span>
                  <span className="ml-2 font-medium text-gray-900 capitalize">{user.role}</span>
                </div>
                <div>
                  <span className="text-gray-600">Status:</span>
                  <span className={`ml-2 font-medium ${user.isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {user.department && (
                  <div>
                    <span className="text-gray-600">Department:</span>
                    <span className="ml-2 font-medium text-gray-900">{user.department}</span>
                  </div>
                )}
                {user.phoneNumber && (
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <span className="ml-2 font-medium text-gray-900">{user.phoneNumber}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <PermissionsSelector
                selectedPermissions={formData.permissions || []}
                onChange={(permissions) => setFormData({ ...formData, permissions })}
                disabled={loading}
                role={user.role}
              />
            </div>

            {/* Notification Preferences */}
            <div>
              <NotificationPreferences
                preferences={formData.notificationPreferences || {}}
                onChange={(notificationPreferences) =>
                  setFormData({ ...formData, notificationPreferences })
                }
                disabled={loading}
              />
            </div>

            {/* User Statistics */}
            {user.statistics && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Activity Statistics</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-primary-600">
                      {user.statistics.totalActions}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Total Actions</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {user.statistics.approvals}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Approvals</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">
                      {user.statistics.rejections}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">Rejections</div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Permissions'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
